import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { Banner, Button, Icon } from '@repo/design-system';
import type { DropZoneRejectReason, DropZoneTarget } from '@repo/design-system';
import { focus, inset, radius, semantic, space, typeRole } from '@repo/design-tokens';
import {
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
  TIMED_OUT_MESSAGE,
  bulkUploadTwinAssets,
  checkTwinAssetRegister,
  getTokenBounded,
  isBiUnreadableError,
} from '@repo/front-end-services';
import type {
  BulkUploadTwinAssetsResult,
  CheckTwinAssetRegisterResult,
} from '@repo/front-end-services';
import {
  ASSET_CSV_MAX_BYTES,
  ASSET_CSV_MAX_ROWS,
  AssetCsvReadError,
  VALUE_PROBLEM_KINDS,
  applyFixes,
  buildMappedCsv,
  buildProblemsCsv,
  checkAssetValues,
  columnLabel,
  columnMapCounts,
  columnMapReady,
  decideColumn,
  editKey,
  leftOutEntries,
  parseAssetCsv,
  downloadProblemSentences,
  suggestColumnMap,
  translateAnswerLines,
} from '@repo/front-end-utils';
import type {
  AdoptionValueContext,
  AssetCsvContract,
  AssetValueEdits,
  ColumnMapEntry,
  LeftOutLines,
  ParsedAssetCsv,
  RegisterNameRow,
  ServerRefusal,
  ValueKindGroup,
  ValueProblemKind,
} from '@repo/front-end-utils';
import { saveAs } from 'file-saver';
import { CheckCard } from '@repo/ui';

import Config from '../../common/Config';
import AssetCsvTemplateButton from './AssetCsvTemplateButton';
import EquipmentStart from './EquipmentStart';
import EquipmentUpload from './EquipmentUpload';
import { LateRefusalStatus } from './LateRefusalStatus';
import type { UploadStep } from './EquipmentUpload';
import type { SeenValueLines } from './EquipmentUploadValues';
import { convertedSentence, uploadOffered, valuesEnteredSentence } from './EquipmentUploadValues';
import readFileText from './readFileText';
import {
  AssetCsvTemplateDownloadError,
  useAssetCsvTemplateDownload,
} from './useAssetCsvTemplateDownload';

interface Props {
  className?: string;
  /** Onboarding-run provenance recorded against each uploaded asset. */
  sessionId: string;
  /**
   * The twin whose Asset Register these assets belong to — REQUIRED, because the
   * register does. `OnboardingPage` already returns early without a `?twinId=`,
   * so this was never actually optional in practice; upload rejects a missing one.
   */
  twinId: number;
  /**
   * Called after a successful upload with the created assets — and the contract it was read
   * against. `upload.fileName` is the chosen file's name, for the Equipment
   * frame's file card (`equipment-page-stage`, R6). `upload.startToken` is what `onUploadStart`
   * returned for THIS upload, when it returned anything.
   */
  onUploaded?: (
    result: BulkUploadTwinAssetsResult,
    upload: {
      mode: 'customer' | 'adopt';
      fileName: string;
      startToken?: number;
      /**
       * The rows this upload wrote, in the answer's order, and the import record it wrote — for the
       * Rows view's *From this import* (`equipment-page-reimport`, R11). `importId` is absent from a
       * middleware that predates the record.
       */
      items: BulkUploadTwinAssetsResult['items'];
      importId?: number;
    },
  ) => void;
  /**
   * Called when an upload THREW, so the host can re-read a register that may have changed anyway.
   *
   * ⚠ A failed upload does NOT mean nothing was written. The importer commits in chunks and has two
   * throw sites that fire after a partial commit, both of which say so in their own message:
   * "N row(s) had already been written" when a chunk fails for a non-row-specific cause, and
   * "N row(s) were written" when it stops rather than retry thousands of writes against a failing
   * database. Both raise a 503 that lands here.
   *
   * So this is deliberately NOT a mirror of `onUploaded`: it carries no result, because there is
   * none, and it does not ask the host to discard anything. Its whole job is to say "the register
   * may have moved under you" — which is exactly when it must be re-read rather than assumed
   * unchanged, because the operator's next move from a failure message is a re-upload, and that is
   * not idempotent for any row without a customer reference.
   */
  onUploadFailed?: (startToken?: number) => void;
  /**
   * Does this twin's register already hold rows? Seeds whether the import controls open collapsed.
   *
   * `null`/absent means NOT YET KNOWN — the read is in flight, or the host does not track it — and
   * is treated as "no register", so the controls open. Deriving rather than seeding once at mount
   * is deliberate: the register read lands AFTER this component mounts, so a value captured at
   * mount would say "no rows" for every twin and the section would never open collapsed at all.
   *
   * A manual toggle overrides it for as long as the operator stays on this twin — see `collapsed`.
   */
  registerHasRows?: boolean | null;
  /**
   * The host opens and closes the import controls itself (`equipment-page-stage`, R6, ruling 2):
   * the Equipment frame's file card has *Upload Updated Register*. When given, it replaces the
   * collapse rule above, and the section's own heading and toggle are not drawn. The component
   * stays mounted either way, so the chosen file survives a close. Absent: today's own disclosure.
   */
  importOpen?: boolean;
  /**
   * Called when an upload is SENT, so the host can hold off anything that must not race it — the
   * Equipment step's import undo, whose deletes an in-flight upsert could undo
   * (`equipment-page-undo-import`).
   * The host clears its flag in `onUploaded` / `onUploadFailed`.
   *
   * It may return a token, which is handed back to THIS upload's own end (`upload.startToken`,
   * `onUploadFailed(startToken)`): an upload outlives the component that sent it, so after a remount
   * two uploads can be open at once and the host needs to tell them apart (Copilot, PR #2060).
   */
  onUploadStart?: () => number | void;
  /** Disables the upload and the mode switch — set by the host while an import undo is in flight. */
  disabled?: boolean;
  /**
   * Incremented by the host after it undoes an import: forget the chosen file and the last
   * result, reopen the import controls and focus the drop zone, in the undone upload's mode
   * (`resetMode`) — after undoing an adoption, the next file is another adoption file. The
   * initial value does nothing.
   */
  resetToken?: number;
  /**
   * The mode the reset reopens in: the undone upload's own (`equipment-page-undo-import`, R19).
   * Without it the reset keeps the mode it finds — which, after a reload, is customer even when the
   * upload undone was an adoption (Copilot, PR #2378).
   */
  resetMode?: 'customer' | 'adopt';
  /** An extra sentence for the upload-failure banner, supplied by the host when it applies. */
  failureNote?: string;
  /**
   * The empty register's start screen (`equipment-page-start`, frame 01): set by the host exactly
   * while the register is KNOWN to be empty, no refused rows are outstanding and the plan holds no
   * equipment the register lacks (R1, and the plan-first exclusion — a twin whose plan came first
   * keeps today's body with the list's *Add N to the register*). While set,
   * this renders the drop zone under `target`, the four tiles and the three links, with today's
   * check and upload beneath the zone once a file is chosen (R9) — instead of the disclosure, the
   * re-upload rules, the drop area and the mode switch.
   *
   * The host keeps this component mounted at the same place either way, so a chosen file, a check
   * or an upload in flight survives the register changing under it (R2). Absent: today's body.
   */
  start?: {
    target: DropZoneTarget;
    /** The start screen's Skip (R6). */
    onSkip: () => void;
    /** The add form (R6). */
    onAddAsset: () => void;
    /**
     * The register's own write messages, which the register list draws when it is shown: the
     * start screen replaces the list, so it says them instead — a failed write, say. Same titles
     * and tones as the list.
     */
    notice?: string | null;
    /**
     * The latest upload's late-refusal line (`equipment-page-reimport`, R12): an upload that wrote
     * nothing leaves the register empty, so this screen, not the summary, says it.
     */
    lateRefusal?: string | null;
    error?: string | null;
    /** Moves on every new error, so the same sentence twice is announced twice. */
    errorKey?: number;
  };
  /**
   * The twin's own BID, which an adoption file's `bid` cells must start with
   * (`equipment-page-adoption`, R5). Blank or absent: the Values step refuses every BID, as the
   * importer does for a twin with none.
   */
  twinBid?: string;
  /**
   * The twin the rows go to, for the column map when it opens in the BODY — an adoption file over a
   * register with rows (`equipment-page-adoption`, R5). The start screen brings its own (`start`).
   */
  bodyTarget?: DropZoneTarget;
  /**
   * The twin's register rows as the value check names against them (`equipment-page-reimport`, R3):
   * an updated file's *Will be* names are numbered against the names already on the twin. Absent or
   * empty: the empty register's numbering, exactly as before.
   */
  registerRows?: readonly RegisterNameRow[];
}

const NO_REGISTER_ROWS: readonly RegisterNameRow[] = [];

/**
 * The disclosure's body, named once so the toggle's `aria-controls` and the region it controls
 * cannot drift apart — exported, because a host that opens the controls itself (`importOpen`)
 * points its own toggle at it (`equipment-page-stage`). A literal rather than a generated id: this
 * component mounts once per step.
 */
export const ASSET_IMPORT_BODY_ID = 'asset-import-controls';

/**
 * Where the pre-upload check of the CHOSEN file stands (`register-preflight-check`, FR4–FR5).
 *
 * One discriminated state rather than a flag per banner, so "Upload is on" has one expression to
 * read and a failed check can never fall through to the clean branch: only `checked` carries a
 * result, and it exists only after a 200 whose body was the envelope.
 *
 *  - `refused` — the importer refused the FILE (400/413: no name column, too large…). The same file
 *    gets the same answer, so there is no *Check again*.
 *  - `failed` — the check could not run (offline, timed out, 401/403/404/429/5xx, unreadable).
 *    Upload stays off, with *Check again* (Rob, 2026-09-28).
 */
type CheckState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  /** `twinId` is the twin the answer is ABOUT — see `answer` in the component. */
  | { kind: 'checked'; result: CheckTwinAssetRegisterResult; twinId: number }
  | { kind: 'refused'; message: string }
  | { kind: 'failed'; message: string }
  /**
   * An adoption check refused because BI could not be read (`equipment-page-adoption`, R6): 503
   * `bi-unreadable`. Nothing was written, and *Retry* asks again.
   */
  | { kind: 'bi-unreadable' };

const IDLE: CheckState = { kind: 'idle' };

/**
 * The transport's sentence, ended as one. A gateway's bare "Service Unavailable" has no full stop,
 * and the banner goes straight on to "Nothing has been uploaded." — seen in the A/B capture. A
 * closing bracket does not end a sentence either: "…(the server's reply could not be read)" is the
 * commonest sentence on this path (correctness review).
 */
const asSentence = (message: string): string =>
  /[.!?…]$/.test(message.trim()) ? message.trim() : `${message.trim()}.`;

const isCsv = (file: File): boolean =>
  file.name.toLowerCase().endsWith('.csv') ||
  file.type === 'text/csv' ||
  file.type === 'application/vnd.ms-excel';

/**
 * A customer file read in the browser on the start screen, and the column decisions made about it
 * (`equipment-page-upload-columns`). Its existence is what puts the column map on screen.
 */
interface ColumnMapping {
  parsed: ParsedAssetCsv;
  entries: readonly ColumnMapEntry[];
}

/**
 * The file the importer actually receives for a mapped upload — rewritten in canonical headers —
 * and the map from its lines back to the customer's (R12). Built when *Check Values* is pressed and
 * dropped on any change to the mapping, so a check answer is only ever about the decisions shown.
 */
interface MappedUpload {
  file: File;
  lineMap: Map<number, number>;
  /** The customer's file it was rewritten from: a mapped upload only ever sends for THAT file. */
  source: File;
}

const ROWS_FORMAT = new Intl.NumberFormat('en-GB');

/** R6's sentences: the card's title, and what the status region says (criterion 25). */
const BI_CHECK_TITLE = 'We couldn’t read BI, so nothing has been uploaded';
const BI_CHECK_SENTENCE = 'We couldn’t read BI. Nothing has been uploaded.';
const BI_UPLOAD_SENTENCE = 'We couldn’t read BI. Nothing was uploaded.';

const NO_EDITS: AssetValueEdits = new Map();
const NOTHING_LEFT_OUT: LeftOutLines = new Map();
/** The pause in edits before the rewritten file is checked again (`upload-values`, R10). */
const VALUES_CHECK_PAUSE_MS = 400;

/** A decision as the status region says it (R15): *Room → customer notes.* / *Owner left out.* */
const decisionSentence = (entry: ColumnMapEntry): string =>
  entry.column === null
    ? `${entry.display} left out.`
    : `${entry.display} → ${
        entry.column === 'customer_notes'
          ? 'customer notes'
          : columnLabel(entry.column).toLowerCase()
      }.`;

const AssetCsvUploader: React.FC<Props> = ({
  className,
  sessionId,
  twinId,
  onUploaded,
  onUploadFailed,
  registerHasRows,
  importOpen,
  onUploadStart,
  disabled = false,
  resetToken,
  resetMode,
  failureNote,
  start,
  twinBid,
  bodyTarget,
  registerRows = NO_REGISTER_ROWS,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const fileInputRef = useRef<HTMLInputElement>(null);
  /*
   * The disclosure's own two elements, held so an automatic collapse can REPAIR the focus it is
   * about to destroy — see the effect below.
   */
  const disclosureToggleRef = useRef<HTMLButtonElement>(null);
  const importBodyRef = useRef<HTMLDivElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The error is a refused file, not a failed upload — see `rejectFiles`. */
  const [errorIsRefusal, setErrorIsRefusal] = useState(false);
  /** Moves with every new error, keying its alert so a repeated sentence is announced again. */
  const [errorSeq, setErrorSeq] = useState(0);
  const [result, setResult] = useState<BulkUploadTwinAssetsResult | null>(null);
  /**
   * THE OPERATOR'S OWN ANSWER about the import controls, or `null` for "they have not said".
   *
   * Held as an override on top of a derived default rather than as the state itself, because the
   * two have different lifetimes: the default follows the register (empty → open, full → closed,
   * re-derived as the read lands), while a choice the operator made must survive the next read
   * that answers a fraction of a second later. Storing one boolean would make every refresh
   * re-decide a question the operator had already answered.
   */
  const [disclosureOverride, setDisclosureOverride] = useState<boolean | null>(null);
  /**
   * Whether this upload is an ADOPTION of a TwinLabs-maintained register
   * (`asset-register-adopt-existing`, ruling O7).
   *
   * A MODE the operator enters, not a checkbox on the default upload — FR1. The distinction is
   * behavioural, not cosmetic: a mis-ticked box would silently let a customer file write TwinLabs
   * identifiers, which is the failure the refusal list exists to prevent, and several real
   * customer registers carry a `bid` column. Switching therefore repaints the whole surface (its
   * own banner, its own button, its own contract) so the operator cannot be in this mode without
   * seeing it — and it CLEARS the chosen file, so a file picked while reading the customer
   * contract can never be uploaded under this one.
   */
  const [adopting, setAdopting] = useState(false);
  /** The column contract the chosen file is read against (`equipment-page-adoption`, R5). */
  const contract: AssetCsvContract = adopting ? 'adopt' : 'customer';
  /** An adoption file's cells are checked against this twin's BID (R5). */
  const adoption = useMemo<AdoptionValueContext | undefined>(
    () => (adopting ? { twinBid: twinBid ?? '' } : undefined),
    [adopting, twinBid],
  );
  /**
   * The last upload was refused because BI could not be read (R6): nothing was written, so the file
   * and every decision about it are KEPT, and *Retry* sends the same upload again.
   */
  const [biUploadRefused, setBiUploadRefused] = useState(false);
  /**
   * The importer's verdict on the CHOSEN file, asked for before anything is written
   * (`register-preflight-check`). It replaced this component's own CSV reader, which peeked at the
   * first 64 KB with a comma splitter that was not quote-aware and mirrored the importer's
   * comment-line rule by hand. Now the importer is the only CSV reader, and the re-upload warning
   * (FR6) reads its count.
   */
  const [check, setCheck] = useState<CheckState>(IDLE);
  /**
   * Monotonic token for the check. Two checks have no completion ordering, so choosing file A then
   * quickly file B could let A's answer land last and decide B's Upload button. Every change that
   * supersedes a check — another file, *Choose a different file*, a mode switch, the host's reset, a
   * new twin — moves the token, and an answer for an older token is dropped.
   */
  const checkToken = useRef(0);
  /*
   * The last mode switch, said in the polite status region while no file is chosen
   * (`equipment-page-start`, criterion 15). On the start screen only the zone's title changes when
   * the mode does — the control the operator pressed stays put — so without this a screen-reader
   * user would hear nothing.
   */
  const [modeAnnouncement, setModeAnnouncement] = useState('');
  /** *Download Template* in the start screen's drop zone (R10): the same hook as the button. */
  const templateDownload = useAssetCsvTemplateDownload();
  /** In adoption mode, *Download Template* fetches the adoption template for this twin (R12). */
  const templateOptions = adopting ? { mode: 'adopt' as const, twinId } : undefined;

  /*
   * ── THE COLUMN MAP (`equipment-page-upload-columns`, frame 02) ───────────────────────────────
   *
   * On the start screen, a file is read here, in the browser, by the importer's own grammar, and
   * every header is decided before anything is sent. An ADOPTION file takes the same route from the
   * body too (`equipment-page-adoption`, R5), under the adoption contract. Only a customer file over
   * a register with rows keeps today's route — the file goes to the importer as chosen.
   */
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [mapped, setMapped] = useState<MappedUpload | null>(null);
  const [uploadStep, setUploadStep] = useState<UploadStep>('columns');
  const [reading, setReading] = useState(false);
  /** A file refused before it was read or as it was read (R3): said in the start screen's alert. */
  const [readRefusal, setReadRefusal] = useState<string | null>(null);
  const [readRefusalSeq, setReadRefusalSeq] = useState(0);
  /** Supersedes a read still running, as `checkToken` does a check. */
  const readToken = useRef(0);
  /** The polite status region's column-map sentence (R15): the read, then each decision. */
  const [mappingAnnouncement, setMappingAnnouncement] = useState('');
  /*
   * ── THE VALUES STEP (`equipment-page-upload-values`, frame 03) ───────────────────────────────
   *
   * The person's own value for a cell (R6), and the lines they left out, each with the kinds it was
   * in (R14). Nothing is sent until Upload: both only rewrite the file the server checks and then
   * receives. Keyed on the column map, so both go whenever it changes.
   */
  const [edits, setEdits] = useState<AssetValueEdits>(NO_EDITS);
  const [leftOut, setLeftOut] = useState<LeftOutLines>(NOTHING_LEFT_OUT);
  /** The status region's Values sentence (R18). */
  const [valuesAnnouncement, setValuesAnnouncement] = useState('');
  /** A sentence said twice running still changes the region's text, so it is said again. */
  const sayValues = (sentence: string) =>
    setValuesAnnouncement(current =>
      sentence !== '' && current === sentence ? `${sentence}\u00a0` : sentence,
    );
  /** A file clean on entering Values is checked at once; after that, after a pause in edits. */
  const checkAtOnce = useRef(false);
  /** Values was just entered: the first checks' answer is said, and decides `checkAtOnce`. */
  const valuesEntered = useRef(false);
  /** The kinds' lines the Values cards have shown, kept across a visit to Columns (R11). */
  const valuesSeen = useRef<SeenValueLines>(new Map());
  /** The server's last refusals, shown pending while an edit's re-check is out (R10). */
  const [heldRefusals, setHeldRefusals] = useState<readonly ServerRefusal[]>([]);
  const forgetValues = () => {
    setEdits(NO_EDITS);
    setLeftOut(NOTHING_LEFT_OUT);
    setValuesAnnouncement('');
    setHeldRefusals([]);
    valuesSeen.current = new Map();
    checkAtOnce.current = false;
    valuesEntered.current = false;
  };

  /**
   * ── THE IMPORT CONTROLS' DISCLOSURE (Rob, 2026-09-22) ─────────────────────────────────────────
   *
   * The step is visited twice and the visits want opposite things. The first time, importing IS
   * the task. Every time after — editing a row, deleting one, resolving what never matched — the
   * format contract and the drop zone are a screenful to scroll past before the register, which is
   * the content by then.
   *
   * Derived, with the operator's own answer on top. `Boolean(...)` rather than `=== true` so the
   * not-yet-known read opens the controls: on a twin with no register that is right, and on one
   * with a register the read lands moments later and closes them, which is a section settling
   * rather than a wrong answer standing.
   */
  const collapsed =
    importOpen !== undefined ? !importOpen : (disclosureOverride ?? Boolean(registerHasRows));
  /** A mapped upload sent from the body's column map, pressed with focus on it (see below). */
  const focusLeftWithBodyMapRef = useRef(false);

  /*
   * ── THE RE-IMPORT ROUTE (`register-reimport-route`, Rob 2026-09-30) ───────────────────────────
   *
   * Collapsed over a register that is already in, the toggle stops reading "Show" and names the
   * route: *Upload an updated register*. Rob found no signposted way back in once the first import
   * had folded the controls away.
   *
   * An upload that WROTE rows counts as "has rows" on its own: straight after an in-session upload
   * the register is in, even before the host's re-read has landed and flipped `registerHasRows`.
   * A result that wrote nothing — every row refused — proves nothing, and the register may still
   * be empty (review, `register-reimport-route`).
   */
  const uploadWroteRows = result !== null && result.created + (result.updated ?? 0) > 0;
  const offerUpdate = collapsed && (registerHasRows === true || uploadWroteRows);
  /** The disclosure's own word, named once: the visible label, and the root of its accessible name. */
  const disclosureWord = collapsed ? 'Show' : 'Hide';

  /*
   * An import closes the controls it came from — Rob's "collapses once the csv has been imported".
   *
   * It writes the OVERRIDE rather than a plain `setCollapsed`, so it also discards a manual
   * *open* made before the upload: the operator opened the controls in order to upload, and that
   * intent is spent the moment the upload lands. `result` is the dependency, so re-opening by hand
   * afterwards is not undone — nothing here fires again until the next upload.
   */
  useEffect(() => {
    if (result === null) return;
    /*
     * NOT when the same update also raised an error — the upload's `catch` pins the controls open
     * so its message stays visible, and a result set just before a throw (the host's `onUploaded`
     * failing) must not fold that message away (Copilot, PR #1839). Read here rather than made a
     * dependency: they land in one batched update, and re-running on `error` alone would collapse
     * the controls whenever an error cleared over an old result.
     */
    if (error) return;
    /*
     * FOCUS IS REPAIRED BEFORE THE SUBTREE GOES, and the order inside this effect is the whole of
     * it: the operator pressed *Upload CSV* with the keyboard, so the focused element is inside the
     * body this same setter is about to unmount. Without the move, focus falls back to
     * `document.body` and the next Tab restarts from the top of the page — while the outcome they
     * are meant to act on has appeared below. Invisible to a mouse user, total to a keyboard one.
     *
     * Gated on the focus actually being INSIDE the body, because stealing it is its own defect: an
     * operator who has tabbed down to the register table must not be yanked back up here by an
     * upload completing. This is a repair for focus this component destroys, not a claim on focus
     * generally. (Copilot, PR #1762.)
     *
     * The toggle is the target because it is the one control that survives the collapse and is
     * what undoes it. Called before the state change rather than in a later effect — the button is
     * already mounted, so there is nothing to wait for.
     */
    // A column map in the body (an updated register or an adoption file) is not inside the body
    // either, and has already gone with the file it sent: focus on its Upload is repaired too.
    const fromBodyMap = focusLeftWithBodyMapRef.current;
    focusLeftWithBodyMapRef.current = false;
    if (importBodyRef.current?.contains(document.activeElement) || fromBodyMap) {
      disclosureToggleRef.current?.focus();
    }
    setDisclosureOverride(true);
    // `error` is read, not depended on — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  /*
   * A different twin decides afresh. The override is an answer about THIS register; carrying it
   * across would open the controls on a twin whose register is already in, or hide them on a twin
   * that has none — the two states this feature exists to tell apart.
   */
  //
  // And a file still chosen is checked again: the answer was about the OLD twin's register (its
  // BID, its names). On mount no file is chosen, so this asks nothing then.
  useEffect(() => {
    setDisclosureOverride(null);
    // A mapped upload re-checks the file it will SEND; one still being mapped has not been checked.
    if (mapped) runCheck(mapped.file, mapped.lineMap);
    else if (file && !mapping) runCheck(file);
    // `runCheck` is recreated every render and reads this render's twin; the twin is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twinId]);

  /**
   * Ask the importer what it would refuse in this file, writing nothing (FR4.3).
   *
   * ONE deadline spent across both waits — the Auth0 token and the request — so the operator's
   * worst case on *Checking the file…* is 30 s, not 30 s of sign-in plus 30 s of request. The same
   * shape as `AssetCsvTemplateButton`, for the same reason.
   *
   * `lineMap` is given for a mapped file: the importer names lines in the file it received, and
   * every one is translated back to the customer's own before the answer is kept (R12a).
   */
  const runCheck = (selected: File, lineMap?: Map<number, number>) => {
    const token = ++checkToken.current;
    setCheck({ kind: 'checking' });
    const deadline = Date.now() + MIDDLEWARE_REQUEST_TIMEOUT_MS;
    void (async () => {
      try {
        const accessToken = await getTokenBounded(getAccessTokenSilently, deadline - Date.now());
        const remaining = deadline - Date.now();
        if (remaining <= 0) {
          throw Object.assign(new Error(TIMED_OUT_MESSAGE), { name: 'TimeoutError' });
        }
        const answer = await checkTwinAssetRegister(Config.middlewareApi, accessToken, selected, {
          twinId,
          sessionId,
          // The same contract the upload will be read against — the mode is part of the question.
          ...(adopting ? { mode: 'adopt' as const } : {}),
          timeoutMs: remaining,
        });
        if (checkToken.current === token) {
          setCheck({
            kind: 'checked',
            result: lineMap ? translateAnswerLines(answer, lineMap) : answer,
            twinId,
          });
        }
      } catch (err) {
        if (checkToken.current !== token) return;
        const status = (err as { status?: unknown } | null)?.status;
        const message = err instanceof Error ? err.message : 'The file could not be checked.';
        // BI unreadable is read BEFORE the generic failure: a definite non-write with its own card
        // and *Retry* (`equipment-page-adoption`, R6).
        if (isBiUnreadableError(err)) {
          console.error('Register check could not read BI', { status });
          setCheck({ kind: 'bi-unreadable' });
          return;
        }
        // Counts and codes only: never a cell, a name or a BID (NFR3).
        if (status === 400 || status === 413) {
          console.error('Register check refused the file', { status });
          setCheck({ kind: 'refused', message });
        } else {
          console.error('Register check failed', {
            status,
            name: err instanceof Error ? err.name : typeof err,
          });
          setCheck({ kind: 'failed', message });
        }
      }
    })();
  };

  const selectFile = (selected: File | undefined) => {
    if (!selected) return;
    if (!isCsv(selected)) {
      /*
       * The newest choice wins, even a refused one: a read still running for an earlier file must
       * not open its map under this refusal, and a map already open goes — its file was replaced —
       * so the refusal is said on the start screen, where the alert is (review).
       */
      forgetChosenFile();
      setError('Please choose a .csv file');
      setErrorIsRefusal(true);
      setErrorSeq(n => n + 1);
      return;
    }
    // Every customer file goes through the column map, whatever the register holds — the start
    // screen's first file, an adoption file (`equipment-page-adoption`, R5) and an updated register
    // alike (`equipment-page-reimport`, R1). Nothing is sent until its columns and values are clear.
    void readForMapping(selected);
  };

  /** A file the start screen will not map (R3): said in its alert, and the file forgotten. */
  const refuseRead = (message: string) => {
    setReadRefusal(message);
    setReadRefusalSeq(n => n + 1);
    if (fileInputRef.current) fileInputRef.current.value = '';
    // A file chosen from the column map's *Choose a Different File* took the panel — and the
    // focused button — with it; the start screen's *Choose File* is where the next choice is made.
    focusDropZoneRef.current = true;
  };

  /**
   * Read a customer file chosen on the start screen and suggest its column map (R1–R5).
   *
   * Nothing is sent: the importer's check runs only once every column is decided (*Check Values*).
   * The limits are the importer's own, applied before the file is read (10 MB) or as soon as it has
   * been (20,000 rows), so a file the importer would refuse is refused here in the same terms.
   */
  const readForMapping = async (selected: File) => {
    const readId = ++readToken.current;
    // A new choice replaces everything about the last one.
    setFile(null);
    setMapping(null);
    setMapped(null);
    setCheck(IDLE);
    checkToken.current += 1;
    setError(null);
    setResult(null);
    setModeAnnouncement('');
    setReadRefusal(null);
    setMappingAnnouncement('');
    forgetValues();
    // Cleared here, before any refusal: a superseded read returns without touching it (review).
    setReading(false);
    if (selected.size > ASSET_CSV_MAX_BYTES) {
      refuseRead('This file is over 10 MB, the most one upload can take.');
      return;
    }
    setReading(true);
    let parsed: ParsedAssetCsv;
    try {
      parsed = parseAssetCsv(await readFileText(selected));
    } catch (err) {
      if (readToken.current !== readId) return;
      setReading(false);
      // Where it failed, never what the file said.
      console.warn('[equipment-upload] read failed', {
        name: selected.name,
        size: selected.size,
        line: err instanceof AssetCsvReadError ? err.line : undefined,
        // A class name, never content: tells a grammar refusal from a read failure or a bug.
        kind: err instanceof Error ? err.name : typeof err,
      });
      const reason = err instanceof Error ? err.message : 'it could not be read';
      refuseRead(`We could not read this file: ${reason.replace(/[.!?…]$/, '')}.`);
      return;
    }
    if (readToken.current !== readId) return;
    setReading(false);
    if (parsed.records.length === 0) {
      refuseRead('This file has no rows under its header.');
      return;
    }
    if (parsed.records.length > ASSET_CSV_MAX_ROWS) {
      refuseRead(
        `This file has ${ROWS_FORMAT.format(parsed.records.length)} rows; one upload takes up to ` +
          `${ROWS_FORMAT.format(ASSET_CSV_MAX_ROWS)}.`,
      );
      return;
    }
    const entries = suggestColumnMap(parsed, contract);
    const waiting = columnMapCounts(entries).undecided.length;
    setFile(selected);
    setMapping({ parsed, entries });
    setUploadStep('columns');
    // Said once the panel's status region is in the page — see `pendingReadAnnouncement`.
    pendingReadAnnouncement.current =
      `${selected.name} read: ${entries.length} column${entries.length === 1 ? '' : 's'}, ` +
      `${waiting} need${waiting === 1 ? 's' : ''} a decision.`;
  };

  /*
   * The read sentence (R15) is written a moment AFTER the panel mounts. The start screen and the
   * panel each draw the status region, so the panel's copy is a new node; one that mounts already
   * holding its sentence is not reliably announced (review), and the panel moves focus in the same
   * commit. Empty first, then the sentence, is a change the region announces.
   */
  const pendingReadAnnouncement = useRef<string | null>(null);
  const mappingShown = mapping !== null;
  useEffect(() => {
    if (!mappingShown || pendingReadAnnouncement.current === null) return undefined;
    const sentence = pendingReadAnnouncement.current;
    pendingReadAnnouncement.current = null;
    const timer = window.setTimeout(() => setMappingAnnouncement(sentence), 150);
    return () => window.clearTimeout(timer);
  }, [mappingShown]);

  /**
   * One header's column decided (R7). Any change to the map makes the last check about a file that
   * will no longer be sent, so it is forgotten with the rewritten file it was about.
   */
  const decide = (index: number, choice: string) => {
    if (!mapping) return;
    const entries = decideColumn(mapping.entries, index, choice, contract);
    if (entries === mapping.entries) return;
    setMapping({ ...mapping, entries });
    setMapped(null);
    setCheck(IDLE);
    checkToken.current += 1;
    // An edit is keyed on a column this decision may have moved: the values start again.
    forgetValues();
    const decided = entries.find(entry => entry.index === index);
    const sentence = decided ? decisionSentence(decided) : '';
    const becameReady = columnMapReady(entries) && !columnMapReady(mapping.entries);
    setMappingAnnouncement(
      becameReady ? `${sentence} Every column is decided. Check Values is ready.` : sentence,
    );
  };

  /**
   * *Check Values*: the browser's checks run over the mapped file (`upload-values`, R1–R5), and a
   * file they pass is rewritten in canonical headers and sent to the importer's check at once — see
   * the effect below. Unless nothing has changed since it last answered, when the answer stands.
   */
  const toValues = () => {
    if (!mapping || !file || !columnMapReady(mapping.entries) || uploadStep === 'values') return;
    setUploadStep('values');
    valuesEntered.current = true;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0]);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    selectFile(event.dataTransfer.files[0]);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  };

  /**
   * Forget the chosen file and its check, KEEPING the last upload's result on screen.
   *
   * Used on its own when the controls are reopened after an upload: the file just uploaded and its
   * passing check both survive the collapse, so without this the reopened body showed that file
   * with *Upload CSV* live — one click from re-sending it, which adds every row without an
   * identifier a second time. What the upload did is the host's import summary, not this
   * component's.
   */
  const forgetChosenFile = () => {
    setFile(null);
    setCheck(IDLE);
    checkToken.current += 1;
    if (fileInputRef.current) fileInputRef.current.value = '';
    // …and a column map with it: the map IS the chosen file, on the start screen.
    setMapping(null);
    setMapped(null);
    setUploadStep('columns');
    setMappingAnnouncement('');
    forgetValues();
    readToken.current += 1;
    setReading(false);
    setBiUploadRefused(false);
  };

  /*
   * The same rule when the HOST reopens the controls (`importOpen` false → true): after an upload,
   * the file just sent must not be sitting there with *Upload CSV* live. The host's toggle does not
   * pass through the bar's `onClick` above, so it gets its own edge (review, `equipment-page-stage`).
   */
  const importOpenRef = useRef(importOpen);
  useEffect(() => {
    const before = importOpenRef.current;
    importOpenRef.current = importOpen;
    if (before === false && importOpen === true && result !== null) forgetChosenFile();
    // `result` is read, not depended on: only the reopening edge forgets the file.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importOpen]);

  const clearFile = () => {
    forgetChosenFile();
    setError(null);
    setResult(null);
    setReadRefusal(null);
  };

  /*
   * *Cancel* on the column map (R11c): forget the file and every decision, back to the start screen
   * with focus on *Choose File* — the reset path's focus, which runs once the zone has mounted.
   */
  const cancelMapping = () => {
    clearFile();
    focusDropZoneRef.current = true;
  };

  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);

  /*
   * The host undid the import (`resetToken` moved): start again. Skips the first render — the
   * token's initial value is not a request — and takes `resetMode` (or keeps `adopting` without
   * one). Focus goes to the drop zone,
   * the control that chooses the next file — but only once the reopened body has actually
   * MOUNTED. A timer here raced React's commit of the reopening and could focus nothing, leaving
   * the operator on <body>; the effect below runs after the commit that mounts the zone.
   */
  const lastResetToken = useRef(resetToken);
  const focusDropZoneRef = useRef(false);
  /*
   * A reset the start screen's arrival still owes focus to. The host bumps the token and re-reads
   * the register in one batch, and the register keeps its rows while that read runs — so the
   * first body to mount can be the OLD one, whose drop area takes the focus and is then replaced
   * by the start screen, leaving the operator on <body> (review). The arrival re-arms the focus.
   */
  const resetAwaitingStartRef = useRef(false);
  useEffect(() => {
    if (resetToken === lastResetToken.current) return;
    lastResetToken.current = resetToken;
    clearFile();
    if (resetMode) setAdopting(resetMode === 'adopt');
    setDisclosureOverride(false);
    focusDropZoneRef.current = true;
    resetAwaitingStartRef.current = true;
    // `clearFile` is recreated every render and only sets state; the token is the trigger, and the
    // host sets `resetMode` in the same update as it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);
  useEffect(() => {
    if (!focusDropZoneRef.current || !dropZoneRef.current) return;
    focusDropZoneRef.current = false;
    /*
     * On the start screen the zone is a group, not a control: its keyboard route is *Choose File*,
     * the first button in its card (`DropZone`), so that is where the next file is chosen from.
     */
    const target = start
      ? dropZoneRef.current.querySelector<HTMLButtonElement>('.tl-dz-actions button')
      : dropZoneRef.current;
    target?.focus();
  });

  /*
   * The start screen arriving means the register is known to be empty (the host's R1) — reached
   * after *Remove all*, an undo, or an upload that wrote nothing. An upload's result still held here
   * describes rows that are no longer on the register, so it goes — and so does the file that upload
   * SENT, which would otherwise sit under
   * the zone checked, with *Upload CSV* live, one press from adding every row again (seen on the
   * real app after *Remove all*). With no landed upload the chosen file, its check and any error
   * stay: a failed upload's banner must still say what happened on the start screen it returns to
   * (R9), and a re-read must not drop a file just chosen.
   */
  const startShown = Boolean(start);
  const startShownRef = useRef(startShown);
  useEffect(() => {
    const before = startShownRef.current;
    startShownRef.current = startShown;
    if (before || !startShown) return;
    if (result !== null) forgetChosenFile();
    setResult(null);
    // A template failure from an earlier visit to this screen is not a current one (review).
    templateDownload.reset();
    /*
     * FOCUS REPAIR ON ARRIVAL. The start screen replaces whatever body showed during the register's
     * re-read, so a control focused there — the old drop area after an import undo or after a failed
     * upload, the list's *Dismiss* on the last refused row — leaves with it and the operator lands on
     * <body> (review; Copilot, PR #2148). Where a reset is owed focus, or focus has been lost,
     * *Choose File* takes it, now: the zone is committed, and waiting for another render would rely on
     * the state setters above causing one.
     */
    const active = document.activeElement;
    const focusLost = !active || active === document.body || !active.isConnected;
    if (resetAwaitingStartRef.current || focusLost) {
      resetAwaitingStartRef.current = false;
      focusDropZoneRef.current = false;
      dropZoneRef.current?.querySelector<HTMLButtonElement>('.tl-dz-actions button')?.focus();
    }
    // `result` and the hook are read, not depended on: only the start screen's arrival acts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startShown]);

  /**
   * Enter or leave adoption mode, discarding whatever was chosen under the other contract.
   *
   * The clear is the safety property, not tidiness: without it an operator could pick a customer
   * file, switch mode, and upload it as an adoption — the exact "a customer file writes TwinLabs
   * identifiers" outcome FR1 rules out, reached by a route a checkbox would have made easier.
   */
  const setMode = (next: boolean) => {
    if (next === adopting) return;
    clearFile();
    setAdopting(next);
    setModeAnnouncement(
      next ? 'Adoption file: the BIDs and references in the file are kept.' : 'Customer file.',
    );
  };

  /*
   * The drop zone's refusals, said in the upload's own error banner (R3) — but as a REFUSAL: the
   * host's `failureNote` is about rows a failed upload wrote, which a refused drop never did, so it
   * is not appended (review). Each refusal moves `errorSeq`, which keys the alert, so dropping the
   * same wrong file twice is announced twice.
   */
  const rejectFiles = (_files: File[], reason: DropZoneRejectReason) => {
    // The newest choice wins over a read still running (see `selectFile`).
    forgetChosenFile();
    setError(reason === 'count' ? 'Drop one file at a time.' : 'Please choose a .csv file');
    setErrorIsRefusal(true);
    setErrorSeq(n => n + 1);
  };

  // Whether the importer's check for this file is in flight (the Values step's *Checking…*).
  const checking = check.kind === 'checking';
  /*
   * The answer, but only while it is about THIS twin. The re-check on a twin change runs in an
   * effect, after a paint; keyed here, the old twin's verdict is never shown for that paint either
   * (frontend review). The mode needs no key: switching it clears the file.
   */
  const answer = check.kind === 'checked' && check.twinId === twinId ? check.result : null;
  const unidentified = answer !== null && answer.rowsWithIdentifier === 0;
  /*
   * The column map warns before the check when NO header fills Asset Identifier (R9), so the check's
   * own warning would say it twice. A header that fills it with blank cells is not warned about by
   * the map, so the check's warning stands for that file (Copilot, PR #2170).
   */
  const mapWarnedNoIdentifier =
    mapping !== null && !columnMapCounts(mapping.entries).identifierMapped;

  /*
   * ── THE VALUES STEP'S GATE (`equipment-page-upload-values`, R10) ──────────────────────────────
   *
   * The browser's checks first, over the current edits; the server is asked only once they find
   * nothing. Its answer counts only while it is about exactly the file built from these edits —
   * every change drops `mapped` and the check — and on this route EVERY refusal it names blocks
   * Upload, tagged or not, shown on a card.
   */
  const browserCheck = useMemo(
    () =>
      mapping && uploadStep === 'values'
        ? checkAssetValues(
            mapping.parsed,
            mapping.entries,
            edits,
            leftOut,
            [],
            adoption,
            registerRows,
          )
        : null,
    [mapping, uploadStep, edits, leftOut, adoption, registerRows],
  );
  const browserClean = browserCheck !== null && browserCheck.kinds.length === 0;
  const valuesAnswer = mapped && browserClean ? answer : null;
  /*
   * Until the re-check answers, the last refusals stand: a refused row stays an error card while
   * it is checked again, rather than reading as fixed and closing under the person's hands.
   */
  const refusals = useMemo(
    () =>
      valuesAnswer
        ? valuesAnswer.rejected.map(({ line, column, reason }) => ({ line, column, reason }))
        : heldRefusals,
    [valuesAnswer, heldRefusals],
  );
  const valueCheck = useMemo(() => {
    if (!mapping || !browserCheck || refusals.length === 0) return browserCheck;
    return checkAssetValues(
      mapping.parsed,
      mapping.entries,
      edits,
      leftOut,
      refusals,
      adoption,
      registerRows,
    );
  }, [mapping, browserCheck, refusals, edits, leftOut, adoption, registerRows]);
  const valuesReady = uploadOffered(valueCheck, valuesAnswer);
  /** Waiting out the pause or asking: Upload is busy, not disabled, so it can hold focus. */
  const valuesChecking =
    browserClean &&
    valuesAnswer === null &&
    check.kind !== 'failed' &&
    check.kind !== 'refused' &&
    check.kind !== 'bi-unreadable';

  /*
   * Entering Values (R18): the first checks' answer is said, and a file they pass goes to the server
   * at once. Declared before the effect that sends it, so it has decided by the time that runs.
   */
  useEffect(() => {
    if (!valuesEntered.current || !browserCheck) return;
    valuesEntered.current = false;
    setValuesAnnouncement(valuesEnteredSentence(browserCheck));
    checkAtOnce.current = !mapped && browserCheck.kinds.length === 0;
    // `mapped` is read, not depended on: only the entry's own checks decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserCheck]);

  /*
   * The rewritten file goes to the importer's check once the browser finds nothing: at once on
   * entering Values, and after a 400 ms pause in edits after that. Only the newest edit's check
   * counts — `runCheck`'s token drops an older answer unseen (Failure States, Overtaken).
   */
  useEffect(() => {
    if (!mapping || !file || uploadStep !== 'values' || !browserClean || mapped) return undefined;
    const send = () => {
      const { text, lineMap } = buildMappedCsv(mapping.parsed, mapping.entries, edits, leftOut);
      const rewritten = new File([text], file.name, { type: 'text/csv' });
      setMapped({ file: rewritten, lineMap, source: file });
      runCheck(rewritten, lineMap);
    };
    if (checkAtOnce.current) {
      checkAtOnce.current = false;
      send();
      return undefined;
    }
    const timer = window.setTimeout(() => {
      // The edit's sentence has been said; the region now says the check (Failure States).
      setValuesAnnouncement('');
      send();
    }, VALUES_CHECK_PAUSE_MS);
    return () => window.clearTimeout(timer);
    // `runCheck` is recreated every render and reads this render's twin; these are the triggers.
    // The twin is one: a pause still running when it changes would send for the old twin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping, file, uploadStep, browserClean, mapped, edits, leftOut, twinId]);

  /** Any change to the values: the rewritten file and its answer are about a file no longer sent. */
  const changeValues = (
    nextEdits: AssetValueEdits,
    nextLeftOut: LeftOutLines,
    sentence: string,
  ) => {
    setEdits(nextEdits);
    setLeftOut(nextLeftOut);
    setMapped(null);
    setCheck(IDLE);
    checkToken.current += 1;
    setBiUploadRefused(false);
    sayValues(sentence);
  };

  /*
   * The server's answer for this file (R18): a refusal is said, since it closes Upload under a
   * keyboard user's focus; a pass clears the last sentence so the region says *Ready to upload*.
   */
  useEffect(() => {
    if (!valuesAnswer) return;
    setHeldRefusals(refusals);
    const refused = valueCheck?.kinds.find(group => group.kind === 'server-refused');
    setValuesAnnouncement(refused ? `${refused.title}.` : '');
    // Said once per answer; `valueCheck` follows from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesAnswer]);
  /** *Convert All* (R7). */
  const convertAll = (group: ValueKindGroup) =>
    changeValues(applyFixes(edits, group), leftOut, convertedSentence(group));
  /** One value from *Fix in Place* (R8); said fixed once the checks no longer list it (below). */
  const editValue = (line: number, column: string, value: string) => {
    const next = new Map(edits);
    next.set(editKey(line, column), value);
    editedRef.current = { line, column };
    changeValues(next, leftOut, '');
  };
  /*
   * *Line N fixed.* only once the re-run checks no longer list that line's cell: a value its own
   * rule takes can still repeat an identifier, or be refused for another reason. The held server
   * refusals count too (`valueCheck`), so a cell the server refused says nothing until the re-check
   * answers, and that answer's own sentence reports the outcome.
   */
  const editedRef = useRef<{ line: number; column: string } | null>(null);
  useEffect(() => {
    const edited = editedRef.current;
    if (!edited || !valueCheck) return;
    editedRef.current = null;
    const still = valueCheck.kinds.some(group =>
      group.lines.some(
        each =>
          each.line === edited.line && (each.column === edited.column || each.column === null),
      ),
    );
    if (!still) setValuesAnnouncement(`Line ${edited.line} fixed.`);
  }, [valueCheck]);
  /** *Leave Out* (R8): each line with the kinds it is in now, and always the card's own (R14). */
  const leaveOutLines = (lines: readonly number[], kind: ValueProblemKind) => {
    const next = new Map(leftOut);
    const kindsByLine = new Map<number, Set<ValueProblemKind>>();
    for (const group of valueCheck?.kinds ?? []) {
      for (const each of group.lines) {
        const kinds = kindsByLine.get(each.line) ?? new Set<ValueProblemKind>();
        kinds.add(group.kind);
        kindsByLine.set(each.line, kinds);
      }
    }
    let added = 0;
    for (const line of lines) {
      if (next.has(line)) continue;
      const kinds = new Set<ValueProblemKind>([kind, ...Array.from(kindsByLine.get(line) ?? [])]);
      next.set(
        line,
        VALUE_PROBLEM_KINDS.filter(each => kinds.has(each)),
      );
      added += 1;
    }
    if (added === 0) return;
    changeValues(
      edits,
      next,
      `${ROWS_FORMAT.format(added)} ${added === 1 ? 'row' : 'rows'} left out of this upload.`,
    );
  };
  /**
   * *Download With Problems Column* (R13a): the customer's own file with every edit so far and a
   * *Problems* column, saved in the browser. Nothing is sent, and nothing is logged on a failure.
   */
  const downloadProblems = () => {
    if (!mapping || !file || !valueCheck) return;
    try {
      const text = buildProblemsCsv(
        mapping.parsed,
        mapping.entries,
        edits,
        downloadProblemSentences(
          mapping.parsed,
          mapping.entries,
          edits,
          leftOut,
          refusals,
          adoption,
        ),
      );
      saveAs(
        new Blob([text], { type: 'text/csv;charset=utf-8' }),
        `${file.name.replace(/\.csv$/i, '')} with problems.csv`,
      );
    } catch {
      sayValues('The download didn’t start. Try again.');
    }
  };

  /*
   * *Check again* lives in the banner the retry replaces, so pressing it unmounts the control that
   * has focus and drops the keyboard user on <body> (frontend review). Focus goes to the Upload
   * button — loading, not disabled, while it checks, so it can hold focus, and it is where the
   * operator goes next. After the commit that makes it focusable, like `focusDropZoneRef`.
   */
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const focusUploadRef = useRef(false);
  useEffect(() => {
    if (!focusUploadRef.current || !uploadButtonRef.current) return;
    focusUploadRef.current = false;
    uploadButtonRef.current.focus();
  });
  const checkAgain = () => {
    if (!file) return;
    focusUploadRef.current = true;
    if (mapped) runCheck(mapped.file, mapped.lineMap);
    else runCheck(file);
  };

  const handleUpload = async () => {
    // Defence in depth: the button is already off in every one of these states. Every file is
    // mapped (`equipment-page-reimport`, R1) and held to the Values step's gate (R10).
    if (!file || disabled || !mapping || !valuesReady) return;
    // The upload sends the file its check was about — the rewritten one — and nothing else.
    if (!mapped) return;
    // A rewritten file is only ever sent for the file it was rewritten from (review).
    if (mapped.source !== file) return;
    const sending = mapped;
    // The lines left out on purpose, recorded on the import (R14); the field is sent only with some.
    const leftOutField = leftOutEntries(mapping.parsed, mapping.entries, edits, leftOut);
    setUploading(true);
    setError(null);
    setBiUploadRefused(false);
    const startToken = onUploadStart?.() ?? undefined;
    let landed: BulkUploadTwinAssetsResult | null = null;

    try {
      const accessToken = await getAccessTokenSilently();
      const answered = await bulkUploadTwinAssets(Config.middlewareApi, accessToken, sending.file, {
        sessionId,
        twinId,
        // Sent only in adoption mode; the customer request is byte-identical to the one it always
        // sent. The server never infers the mode from the file.
        ...(adopting ? { mode: 'adopt' as const } : {}),
        ...(leftOutField.length > 0 ? { leftOut: leftOutField } : {}),
      });
      // Every line the importer names is the customer's own from here on (R12a).
      const uploadResult = translateAnswerLines(answered, sending.lineMap);
      setResult(uploadResult);
      /*
       * The map and the rewritten file are spent: the rows are in (or refused), and the file the
       * check passed must not sit one press from being sent again. The original is forgotten with
       * them — the body that replaces the start screen would otherwise offer IT, unchecked as sent.
       */
      // The body's column map goes with the file; focus on its Upload is repaired once the result
      // folds the controls (below). The start screen's own hand-over is the host's.
      if (!start && document.activeElement?.closest('[data-upload]')) {
        focusLeftWithBodyMapRef.current = true;
      }
      forgetChosenFile();
      /*
       * Match review no longer opens by itself after an upload (`equipment-page-landed`): the step
       * lands on *To identify* in the Groups view instead, where a group is one decision.
       */
      landed = uploadResult;
    } catch (err) {
      /*
       * BI could not be read (`equipment-page-adoption`, R6): a DEFINITE non-write — the importer
       * read BI before planning a single row — so the file, the map and the edits are KEPT and
       * *Retry* re-sends this upload. Read before the generic path, which forgets the file because
       * any other failure may have written rows. The host is still told, so it ends its upload.
       */
      if (isBiUnreadableError(err)) {
        console.error('Register upload could not read BI', {
          status: (err as { status?: unknown }).status,
        });
        setBiUploadRefused(true);
        onUploadFailed?.(startToken);
        return;
      }
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setErrorIsRefusal(false);
      setErrorSeq(n => n + 1);
      /*
       * The pressed *Upload CSV* goes with the forgotten file — on the start screen, and in the
       * body's column map (an updated register, an adoption file) — which would drop a keyboard
       * operator on <body>; the retry starts from the drop zone (*Choose File* on the start screen),
       * so focus goes there once it has mounted (review). Only when focus was in the upload: an
       * operator who has moved on keeps their place.
       */
      if (start || document.activeElement?.closest('[data-upload]')) {
        focusDropZoneRef.current = true;
      }
      // The request reached the server and may have written rows before it failed — see the prop's
      // own note. The host re-reads.
      //
      // The file is FORGOTTEN and the controls PINNED OPEN (review, `register-reimport-route`). A
      // failed upload may have committed chunks, so the same file must not stay one click from a
      // re-send that duplicates every row it wrote without an identifier; a retry is an explicit
      // re-choice. The error is kept (`forgetChosenFile` leaves it), and the pin stops the host's
      // re-read of any rows it wrote from folding this failure banner — the only place that says
      // how many rows landed — away behind *Upload an updated register*.
      forgetChosenFile();
      setDisclosureOverride(false);
      onUploadFailed?.(startToken);
    } finally {
      setUploading(false);
    }
    /*
     * OUTSIDE the request's `try`: an error thrown by the host's own success handling is not an
     * upload failure. Inside it, the catch above reported the same upload as failed too — ending
     * its write twice, which re-opened the host's discard while another upload still ran (Copilot,
     * PR #2060). It is still shown and pins the controls open, with the file forgotten, exactly as
     * before (Copilot, PR #1839) — only `onUploadFailed` is not called, because the upload landed.
     */
    if (landed === null) return;
    try {
      onUploaded?.(landed, {
        mode: adopting ? 'adopt' : 'customer',
        fileName: file.name,
        ...(startToken !== undefined ? { startToken } : {}),
        items: landed.items,
        ...(landed.importId !== undefined ? { importId: landed.importId } : {}),
      });
    } catch (hostError) {
      console.error('Register upload: the host could not take the landed upload', hostError);
      setError(hostError instanceof Error ? hostError.message : 'Upload failed. Please try again.');
      setErrorIsRefusal(false);
      setErrorSeq(n => n + 1);
      forgetChosenFile();
      setDisclosureOverride(false);
    }
  };

  /*
   * The check-and-upload controls, named once and rendered by BOTH bodies — the start screen
   * (`equipment-page-start`, R9: beneath the drop zone, exactly as today) and the body over a
   * register with rows — so the two can never say a different thing about the same file.
   */
  const statusRegion = (
    <>
      {/* One polite status region for the check, always mounted so a change is announced
          (FR5.6). The error banners announce themselves as alerts; the no-BID note is a
          status banner of its own. */}
      <div role="status" className="visually-hidden">
        {reading
          ? 'Reading the file.'
          : mapping && uploadStep === 'columns'
            ? /* The column map's sentence (R15): the read, then each decision. */
              mappingAnnouncement
            : mapping && uploadStep === 'values'
              ? /* BI unreadable first (`equipment-page-adoption`, criterion 25), then the Values
                   step's sentences (`upload-values`, R18). */
                biUploadRefused
                ? BI_UPLOAD_SENTENCE
                : check.kind === 'bi-unreadable'
                  ? BI_CHECK_SENTENCE
                  : /* The newest sentence first, so a failure while ready is still said. */
                    valuesAnnouncement ||
                    (valuesReady
                      ? `Ready to upload ${ROWS_FORMAT.format(valueCheck?.rows ?? 0)} assets.`
                      : checking
                        ? 'Checking the file.'
                        : '')
              : !file && modeAnnouncement
                ? modeAnnouncement
                : ''}
      </div>
    </>
  );
  /*
   * BI could not be read (`equipment-page-adoption`, R6), on the check or the upload: an error card
   * in the Values step, with *Retry*. The adoption banner that stood here moved onto the column map
   * as a `Note` (R5).
   */
  const retryBi = () => {
    focusUploadRef.current = true;
    if (biUploadRefused) void handleUpload();
    else checkAgain();
  };
  const biUnreadableCard = file &&
    !result &&
    (biUploadRefused || check.kind === 'bi-unreadable') && (
      <CheckCard
        tone="error"
        open
        icon={<Icon name="circle-exclamation" variant="solid" size="sm" />}
        title={biUploadRefused ? BI_UPLOAD_SENTENCE : BI_CHECK_TITLE}
        where="Each row’s product is read from BI first, so nothing is written until BI answers. The file and your changes are kept."
        actions={
          <Button
            type="button"
            variant="primary"
            size="sm"
            aria-label="Retry reading BI"
            iconLeft={<Icon name="rotate-right" variant="solid" size="xs" />}
            onClick={retryBi}
          >
            Retry
          </Button>
        }
      />
    );
  /*
   * The check's two failures and the no-identifier warning: in the check messages on the routes
   * that keep them, and in the column map's banners slot on Values, where the cards replace the
   * rest (`upload-values`, R10's Failure States row).
   */
  const checkFailedBanners = (
    <>
      {file && !result && check.kind === 'failed' && (
        <Banner surface="on-solid" tone="error" title="We could not check this file.">
          {asSentence(check.message)} Nothing has been uploaded. If you have edited the file since
          choosing it, choose it again.
          <div className="retry-slot">
            <Button variant="ghost" size="xs" onClick={checkAgain}>
              Check again
            </Button>
          </div>
        </Banner>
      )}
      {file && !result && check.kind === 'refused' && (
        <Banner surface="on-solid" tone="error" title="This file cannot be uploaded.">
          {check.message}
        </Banner>
      )}
    </>
  );
  const unidentifiedBanner = file && !result && unidentified && !mapWarnedNoIdentifier && (
    <Banner surface="on-solid" tone="warning" title="These rows cannot be updated later.">
      This file has no <code>customer_asset_identifier</code> values, so we cannot tell a later
      upload's rows from these ones — uploading a corrected file will ADD every row again rather
      than update it. Filling that column in now makes re-uploads safe.
    </Banner>
  );
  /*
   * A failed upload, said where the operator lands after it: the start screen, or the body over a
   * register. (The check's own answers are the column map's Values cards.)
   */
  const uploadFailedBanner = error && (
    <Banner key={errorSeq} surface="on-solid" tone="error" title="Upload failed.">
      {error}
      {failureNote && !errorIsRefusal ? ` ${failureNote}` : null}
    </Banner>
  );

  /*
   * THE START SCREEN (`equipment-page-start`, frame 01): the empty register's whole step. Its
   * arrival clears any upload result left over (see `startShown`).
   */
  if (mapping && file) {
    /*
     * THE COLUMN MAP (`equipment-page-upload-columns`, frame 02), in the start screen's place and
     * INSIDE its `data-start` wrapper: the page's `onUploadStart` arms the start screen's focus
     * hand-over (#2152) by finding the pressed Upload inside `[data-start]`, and the panel's Upload
     * is that button. A file chosen in the BODY — an adoption file (`equipment-page-adoption`, R5) or
     * an updated register (`equipment-page-reimport`, R1) — opens it too, in the body's place, with
     * no `data-start`: the body keeps its own focus rules.
     */
    return (
      <div className={className} data-start={start ? '' : undefined} data-upload="">
        <EquipmentUpload
          fileName={file.name}
          contract={contract}
          parsed={mapping.parsed}
          entries={mapping.entries}
          onDecide={decide}
          step={uploading ? 'upload' : uploadStep}
          onColumns={() => setUploadStep('columns')}
          onValues={toValues}
          checking={valuesChecking}
          ready={valuesReady}
          values={
            valueCheck
              ? {
                  check: valueCheck,
                  edits,
                  leftOut,
                  onConvertAll: convertAll,
                  onEdit: editValue,
                  onLeaveOut: leaveOutLines,
                  seen: valuesSeen,
                  // The server's count, for exactly this file: none is shown before it answers.
                  rowsWithoutBid: valuesAnswer ? valuesAnswer.rowsWithoutBid.length : null,
                }
              : null
          }
          onUpload={() => void handleUpload()}
          onDownload={downloadProblems}
          uploadRef={uploadButtonRef}
          target={start?.target ?? bodyTarget ?? { name: '' }}
          onChooseFile={selectFile}
          onCancel={cancelMapping}
          disabled={disabled}
          download={{
            onClick: () => void templateDownload.download(templateOptions),
            downloading: templateDownload.downloading,
          }}
          banners={
            <>
              <AssetCsvTemplateDownloadError error={templateDownload.error} />
              {start?.error && (
                <Banner
                  key={start.errorKey}
                  surface="on-solid"
                  tone="error"
                  title="That change was not made."
                >
                  {start.error}
                </Banner>
              )}
              {start?.notice && (
                <Banner surface="on-solid" tone="success" title="Register updated.">
                  {start.notice}
                </Banner>
              )}
              {uploadStep === 'values' && (
                <>
                  {checkFailedBanners}
                  {biUnreadableCard}
                  {unidentifiedBanner}
                </>
              )}
            </>
          }
          status={statusRegion}
        />
      </div>
    );
  }

  if (start) {
    return (
      <div className={className} data-start="">
        <EquipmentStart
          adopting={adopting}
          target={start.target}
          onFile={selectFile}
          onReject={rejectFiles}
          download={{
            onClick: () => void templateDownload.download(templateOptions),
            downloading: templateDownload.downloading,
          }}
          onToggleMode={() => setMode(!adopting)}
          onSkip={start.onSkip}
          onAddAsset={start.onAddAsset}
          disabled={disabled}
          dropZoneRef={dropZoneRef}
          below={
            <div
              className="start-below"
              /* Nothing to show yet: the two always-mounted live regions stay in the tree, but
                   the wrapper takes no room, so the zone sits the frame's gap above the tiles. */
              data-empty={
                file ||
                error ||
                readRefusal ||
                templateDownload.error ||
                start.error ||
                start.notice ||
                start.lateRefusal
                  ? undefined
                  : ''
              }
            >
              <AssetCsvTemplateDownloadError error={templateDownload.error} />
              {start.error && (
                <Banner
                  key={start.errorKey}
                  surface="on-solid"
                  tone="error"
                  title="That change was not made."
                >
                  {start.error}
                </Banner>
              )}
              {start.notice && (
                <Banner surface="on-solid" tone="success" title="Register updated.">
                  {start.notice}
                </Banner>
              )}
              {/* The summary's own words and look (R12), said politely as the screen returns: the
                  region is always mounted, and the line put in it a moment after. */}
              <LateRefusalStatus line={start.lateRefusal ?? null} />
              {/* A customer file the column map would not open (R3). An alert, keyed so the same
                  refusal twice is announced twice. */}
              {readRefusal && (
                <Banner
                  key={`read-${readRefusalSeq}`}
                  surface="on-solid"
                  tone="error"
                  title="This file cannot be uploaded."
                >
                  {readRefusal}
                </Banner>
              )}
              {file && (
                <p className="start-file">
                  <strong>{file.name}</strong> · Drop another file or choose again to replace it.
                </p>
              )}
              {uploadFailedBanner}
              {statusRegion}
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className={className}>
      {/*
        THE IMPORT CONTROLS, behind a disclosure.

        The disclosure is hand-rolled, and that is a RULING rather than a shortcut: D18 of the
        design-system epic (Rob, 2026-09-05) allows a token-laid-out disclosure until the Accordion
        card lands. `@repo/design-system` has no disclosure primitive; `Panel` is Fluent-backed and
        `TLAccordion` is a §6A legacy item. §6B's accordion entry names this surface as its THIRD
        consumer, after the asset panel and the (since removed) admin planner's register region, so the card's own
        inventory finds it when that card is designed — a claim in a comment nobody greps would not.
        Nothing here invents a chevron, a card or a border treatment: it is a section, a heading and
        the design system's own Button, exactly as the precedent.
      */}
      <section className="import-controls" aria-label="Import a register">
        {/* The host's own control replaces this bar (`importOpen`, `equipment-page-stage`). */}
        {importOpen === undefined && (
          <div className="import-bar">
            <h4 className="import-title">Import a register</h4>
            <Button
              ref={disclosureToggleRef}
              /* The route is a button, not a text link: it is the way back in. The no-register states
               keep the quiet text form. */
              variant={offerUpdate ? 'ghost' : 'text'}
              size="xs"
              aria-expanded={!collapsed}
              /*
               * OMITTED while the body is absent, rather than pointing at an id nothing renders.
               * The body is conditionally rendered, not hidden, so a collapsed disclosure that kept
               * `aria-controls` would hand assistive technology a relationship to a missing element.
               * `aria-expanded` carries the state on its own. (Copilot, PR #1762, round 2.)
               */
              aria-controls={collapsed ? undefined : ASSET_IMPORT_BODY_ID}
              /* Where the visible label is one word, a screen-reader user tabbing this step would meet
               a bare "Show, button" on a screen that also carries the register's own controls, so
               the accessible name says what it shows. Same rule the admin register region follows.
               The route's visible text already says what it opens, so it carries no override: its
               name IS its label (WCAG 2.5.3, label in name). */
              aria-label={offerUpdate ? undefined : `${disclosureWord} the import controls`}
              onClick={() => {
                if (collapsed && result !== null) forgetChosenFile();
                setDisclosureOverride(!collapsed);
              }}
            >
              {offerUpdate ? 'Upload an updated register' : disclosureWord}
            </Button>
          </div>
        )}

        {/*
          CONDITIONALLY RENDERED, not `hidden`. The precedent uses `hidden`, which is right for a
          region whose content is inert text; this one holds a file input, a drop zone and the
          mode switch, and `hidden` keeps their text in the DOM where a search finds it. The state
          that matters — the chosen file, the upload result — lives in this component, not in the
          markup, so unmounting the body costs the operator nothing.
        */}
        {!collapsed && (
          <div className="import-body" id={ASSET_IMPORT_BODY_ID} ref={importBodyRef}>
            {/* VISUALLY hidden, not `display: none` — that removed the only file control from the
          tab order AND from the accessibility tree, so the Assets step could not be completed
          without a mouse and a screen reader never announced that a file input existed.
          (Multi-agent review, PR #1136, frontend/a11y reviewer.) */}
            <input
              ref={fileInputRef}
              id="asset-csv-input"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileSelect}
              className="visually-hidden"
              aria-label="Choose an Asset Register CSV file"
            />
            {statusRegion}

            {/* The template, alone (`equipment-page-start`, R11): the format is stated in the file
                itself, above its column names, and nowhere else. No result only, as the format
                block it replaces was; in adoption mode, the adoption template
                (`equipment-page-adoption`, R12). */}
            {!result && <AssetCsvTemplateButton mode={contract} twinId={twinId} />}

            <div
              ref={dropZoneRef}
              className={`upload-area ${dragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={!file ? openFilePicker : undefined}
              // The drop zone stays a mouse affordance, but it is no longer the ONLY one: the
              // labelled input above is now reachable by Tab. Keeping the zone operable by keyboard
              // too means either route works.
              role={!file ? 'button' : undefined}
              tabIndex={!file ? 0 : undefined}
              aria-controls={!file ? 'asset-csv-input' : undefined}
              onKeyDown={
                !file
                  ? event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openFilePicker();
                      }
                    }
                  : undefined
              }
            >
              <span className="icon">
                <Icon name="file-arrow-up" variant="solid" size="2xl" />
              </span>
              <div className="message">
                {file ? file.name : 'Drag and drop a CSV, or click to browse'}
              </div>
              {file && (
                <button
                  type="button"
                  className="change-file"
                  onClick={event => {
                    event.stopPropagation();
                    clearFile();
                  }}
                >
                  Choose a different file
                </button>
              )}
            </div>

            {uploadFailedBanner}

            {/*
        The mode switch — a distinct, clearly-labelled action ALONGSIDE the default upload, never a
        checkbox on it (FR1). Below the primary action rather than above it: adoption is the rare,
        deliberate case, and putting it first would present a TL_ADMIN-only act as the normal one.
      */}
            <button
              type="button"
              className="mode-switch"
              disabled={disabled}
              onClick={() => setMode(!adopting)}
            >
              {adopting
                ? 'Cancel — upload a customer’s own register instead'
                : 'Adopt an existing TwinLabs register instead'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default styled(AssetCsvUploader)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 520px;
  margin: 0 auto;

  /* The start screen (equipment-page-start): frame 01's column is the zone's 760 px and the
     tile row's 756 px, centred in the whole main panel rather than the 520 px upload column. */
  &[data-start] {
    position: relative;
    max-width: 800px;
    width: 100%;
    flex: 1;
  }

  /* The column map (equipment-page-upload-columns, frame 02) takes the main card's whole width:
     the map and the 520 px preview sit side by side. */
  &[data-start][data-upload],
  &[data-upload] {
    max-width: none;
    margin: 0;
    width: 100%;
  }

  .start-below {
    width: 100%;
    max-width: 760px;
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
  }

  .start-below[data-empty] {
    position: absolute;
  }

  /* The always-mounted template-failure region takes no gap while it has nothing to say. */
  .start-below > [role='status']:empty {
    position: absolute;
  }

  .start-file {
    margin: 0;
    text-align: center;
    ${typeRole.body1};
    color: ${semantic.text.body};
    overflow-wrap: anywhere;
  }

  .start-file strong {
    color: ${semantic.text.primary};
    font-weight: ${typeRole.body1Strong.fontWeight};
  }

  /*
    The disclosure, laid out with tokens and nothing else (D18). No card, no border, no chevron:
    the controls below already carry the surface's visual identity, and inventing chrome for a
    strip whose design card has not been drawn yet is how two spellings of an accordion end up in
    the estate. When §6B's Accordion card lands, this collapses into it.
  */
  .import-controls {
    display: flex;
    flex-direction: column;
    gap: ${space[4]};
  }

  .import-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space[3]};
  }

  .import-title {
    margin: 0;
    ${typeRole.body1Strong};
    color: ${semantic.text.primary};
  }

  .import-body {
    display: flex;
    flex-direction: column;
    /* The token, not the 1rem the rules around it still hard-code. Same value, and the new rules
       in this release are token-only whatever their neighbours do. (Copilot, PR #1762.) */
    gap: ${space[4]};
  }

  .upload-area {
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    padding: 3rem 2rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: #fafafa;

    &:hover {
      border-color: #00bbcc;
      background-color: #f0fdfe;
    }

    &.drag-over {
      border-color: #00bbcc;
      background-color: #e6fffa;
    }

    &.has-file {
      cursor: default;
      border-color: #10b981;
      background-color: #ecfdf5;
    }

    .icon {
      font-size: 2.5rem;
      color: #707070;
      margin-bottom: 0.75rem;
    }

    .message {
      font-size: 1rem;
      font-weight: 500;
      color: #1a1a1a;
      margin-bottom: 0.25rem;
    }

    .change-file {
      margin-top: 0.5rem;
      background: none;
      border: none;
      color: #00bbcc;
      cursor: pointer;
      font-size: 0.875rem;
      text-decoration: underline;

      &:hover {
        color: #009daa;
      }
    }
  }

  .retry-slot {
    margin-top: ${space[2]};
  }

  /* The adoption mode switch. Token-sourced throughout, unlike its neighbours above — the
     surrounding hex literals predate the design system and are on the migration list rather
     than in this feature's scope, but new styling does not add to them.

     Deliberately quieter than the Upload button above it: this is a rare TL_ADMIN act, and giving it
     equal visual weight to the normal path would invite it. It is still a real button with a
     visible focus ring, because it is the only route into the mode. */
  .mode-switch {
    display: block;
    margin: 0 auto;
    /* inset, not space: the tokens package rules that inset goes INSIDE a control and space
       BETWEEN components. inset[2]/inset[3] also puts the hit area clear of the 24px minimum
       (WCAG 2.2 SC 2.5.8) rather than exactly on it. */
    padding: ${inset[2]} ${inset[3]};
    background: none;
    border: none;
    border-radius: ${radius.sm};
    /* body, NOT muted. This control is deliberately quieter than the Upload button above, but
       semantic.text.muted is ink at 52% alpha — the description-text role — and composites to
       about 2.7:1 on this white panel, which fails WCAG 1.4.3 for 12px text. It is also the ONLY
       route into adoption mode, so an operator who cannot see it silently falls back to the
       default upload, which discards exactly the identifiers this feature exists to keep. body is
       ink-3 at about 4.8:1 and still reads as markedly quieter than the brand-cyan sibling. */
    color: ${semantic.text.body};
    cursor: pointer;
    font-family: ${typeRole.caption1.fontFamily};
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    text-decoration: underline;

    &:hover:not(:disabled) {
      color: ${semantic.text.primary};
    }

    /* Disabled while the host undoes an import (equipment-page-undo-import): it must LOOK
       unavailable and stop reacting to hover, not keep the pointer and the hover darkening of a
       live link. */
    &:disabled {
      color: ${semantic.interactive.disabled};
      cursor: not-allowed;
    }

    /* box-shadow, not outline: focus.ring is the three-layer composition the design system
       defines, and it is what every other focusable control in this file already uses. */
    &:focus-visible {
      outline: none;
      box-shadow: ${focus.ring};
    }
  }

  /* NO BACKTICKS IN A COMMENT IN HERE — one inside a styled-components template literal
     terminates the literal, and the file then fails with a wall of TS1005s pointing at
     unrelated lines. (Tripped exactly that way writing this block; Banner.tsx carries the
     same warning.)

     The two hand-rolled notice treatments that used to live here — .error-message (a
     red-tinted box) and .success-message (a green tick row with its own link colour) — are
     GONE. Both are now Banner from @repo/design-system, the component epic ledger row 22b
     exists to converge these on, and both carried hard-coded hex (#dc2626 / #fef2f2 /
     #fecaca / #047857 / #10b981) that ratchet rule 8 forbids.

     The drop zone and the buttons above keep their legacy hex deliberately: restyling the
     whole step is onboarding-design-system-alignment (ledger 23), and folding it in here
     would mix an unrelated restyle into a behaviour release.

     code is styled because the re-upload warning names the identifier COLUMN, and a bare
     column name in running prose reads as emphasis rather than as an identifier. */
  code {
    background: ${semantic.surface.panelTint};
    border: 1px solid ${semantic.border.faint};
    /* radius.sm, not a 3px literal — the sibling dialog styles the identical chip with the
       token, so a literal here rendered the same treatment at two different radii.
       (Design-system reviewer, PR #1136: the only style literal this diff ADDED.) */
    border-radius: ${radius.sm};
    padding: 0 ${space[1]};
    font-size: 0.9em;
  }

  /* Hidden from sight, present for the keyboard and the accessibility tree — the file input and the
     check's status region. */
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .upload-area:focus-visible {
    outline: none;
    box-shadow: ${focus.ring};
  }
`;
